import React from 'react';
import { Circle, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Duct leakage class — pressurized sealed duct, dashed leak arrows at seams + pressure gauge. */
export function DiagramDuctLeakageClass() {
  const x = 40, y = 95, w = 270, h = 70;
  const cy = y + h / 2;

  // Seam positions
  const seams = [90, 145, 210, 260];

  return (
    <DiagramCanvas>
      {/* Capped duct (pressurization test) */}
      <Rect x={x} y={y} width={w} height={h} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* End caps */}
      <Rect x={x - 6} y={y - 4} width={6} height={h + 8} fill={DT.varColor} opacity={0.6} stroke={DT.stroke} strokeWidth="1" />
      <Rect x={x + w} y={y - 4} width={6} height={h + 8} fill={DT.varColor} opacity={0.6} stroke={DT.stroke} strokeWidth="1" />

      {/* Seam lines (joints) */}
      {seams.map(sx => (
        <Line key={sx} x1={sx} y1={y} x2={sx} y2={y + h}
          stroke={DT.stroke} strokeWidth="0.8" opacity={0.5} strokeDasharray="2,2" />
      ))}

      {/* Internal pressure indicator: + signs scattered */}
      {[80, 130, 180, 230, 270].map((px, i) => (
        <SvgText key={i} x={px} y={cy + 3} fill={DT.varColor}
          fontSize="11" fontFamily={DT.mono} textAnchor="middle" fontWeight="700" opacity={0.55}>+</SvgText>
      ))}

      {/* Leak arrows at seams (top & bottom, dashed) */}
      {seams.map((sx, i) => (
        <G key={`lt${i}`}>
          <Line x1={sx} y1={y} x2={sx + 4} y2={y - 22}
            stroke={DT.varColor} strokeWidth="1.2" strokeDasharray="3,2" />
          <Polygon points={`${sx + 4},${y - 22} ${sx},${y - 18} ${sx + 8},${y - 16}`} fill={DT.varColor} opacity={0.85} />
        </G>
      ))}
      {[110, 175, 240].map((sx, i) => (
        <G key={`lb${i}`}>
          <Line x1={sx} y1={y + h} x2={sx - 4} y2={y + h + 22}
            stroke={DT.varColor} strokeWidth="1.2" strokeDasharray="3,2" />
          <Polygon points={`${sx - 4},${y + h + 22} ${sx},${y + h + 18} ${sx - 8},${y + h + 16}`} fill={DT.varColor} opacity={0.85} />
        </G>
      ))}

      {/* Pressure gauge on cap */}
      <Line x1={x - 3} y1={y - 4} x2={x - 3} y2={y - 24} stroke={DT.leader} strokeWidth="1.2" />
      <Circle cx={x - 3} cy={y - 36} r={11} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
      <Line x1={x - 3} y1={y - 36} x2={x - 8} y2={y - 42} stroke={DT.varColor} strokeWidth="1.5" />
      <SvgText x={x - 3} y={y - 50} fill={DT.varColor} fontSize="9"
        fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔP</SvgText>

      {/* Test fan injecting CFM */}
      <Circle cx={x + w + 30} cy={cy} r={14} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.5" />
      <Circle cx={x + w + 30} cy={cy} r={3} fill={DT.stroke} />
      {[0, 120, 240].map(deg => {
        const rad = deg * Math.PI / 180;
        return (
          <Line key={deg} x1={x + w + 30 + 6 * Math.cos(rad)} y1={cy + 6 * Math.sin(rad)}
            x2={x + w + 30 + 12 * Math.cos(rad + 0.5)} y2={cy + 12 * Math.sin(rad + 0.5)}
            stroke={DT.stroke} strokeWidth="1.5" />
        );
      })}
      <Line x1={x + w + 6} y1={cy} x2={x + w + 16} y2={cy} stroke={DT.arrow} strokeWidth="1.5" />
      <Polygon points={`${x + w + 6},${cy} ${x + w + 12},${cy - 3} ${x + w + 12},${cy + 3}`} fill={DT.arrow} />

      {/* Labels */}
      <LeaderLine x1={145} y1={y - 18} x2={50} y2={210} varName="Q_L" desc="Leakage (CFM/100 ft²)" anchor="start" />
      <LeaderLine x1={x + 30} y1={y + h - 8} x2={150} y2={210} varName="A" desc="Surface area" anchor="middle" />
      <LeaderLine x1={x + w + 30} y1={cy + 14} x2={310} y2={210} varName="CL" desc="Leakage class" anchor="end" />

      <FormulaTag formula="Q_L = CL · A · ΔP^0.65   (cfm / 100 ft²)" />
    </DiagramCanvas>
  );
}
